import { Box, IconButton, Switch, TextField, Typography } from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import LocalizationProviderWithLanguages from '../../Shared/Localization/LocalizationProviderWithLanguages';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { BottomIconButton } from '../Shared/BottomIconButton';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import { InsertDoctorRequest, InsertHolidayRequest, InsertSickRequest, MobileRecordsVovClient } from '../../../ApiOld/Api/ApiServerVov';

interface ICommonRangeActivityResponse {
    ok: boolean;
    message?: string;
}

export default function MobileRangeActivity() {
    const param = useParams();
    const { t } = useTranslation();
    const { authorizedMobileEmployeeApi } = useMobileApi(MobileRecordsVovClient);
    const { enqueueSnackbar } = useSnackbar();
    const rangeActivityType = param.type ?? null;
    const [wholeDay, setWholeDay] = useState(true);
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [endDate, setEndDate] = useState<Date | null>(new Date());

    const insertHoliday = useCallback(async (): Promise<ICommonRangeActivityResponse> => {
        const res = await authorizedMobileEmployeeApi.insertHoliday(new InsertHolidayRequest({
            dateFrom: startDate ? new Date(startDate).toISOString().split('T')[0] : '',
            dateTo: endDate ? new Date(endDate).toISOString().split('T')[0] : '',
            timeFrom: wholeDay ? '' : (startDate ? new Date(startDate).toTimeString().slice(0, 5) : ''),
            timeTo: wholeDay ? '' : (endDate ? new Date(endDate).toTimeString().slice(0, 5) : ''),
        }));
        return { ok: res.ok ?? false, message: res.message };
    }, [startDate, endDate, authorizedMobileEmployeeApi, wholeDay]);

    const insertDoctor = useCallback(async (): Promise<ICommonRangeActivityResponse> => {
        const res = await authorizedMobileEmployeeApi.insertDoctor(new InsertDoctorRequest({
            dateFrom: startDate ? new Date(startDate).toISOString().split('T')[0] : '',
            dateTo: endDate ? new Date(endDate).toISOString().split('T')[0] : '',
            timeFrom: wholeDay ? '' : (startDate ? new Date(startDate).toTimeString().slice(0, 5) : ''),
            timeTo: wholeDay ? '' : (endDate ? new Date(endDate).toTimeString().slice(0, 5) : ''),
        }));
        return { ok: res.ok ?? false, message: res.message };
    }, [startDate, endDate, authorizedMobileEmployeeApi, wholeDay]);

    const insertSick = useCallback(async (): Promise<ICommonRangeActivityResponse> => {
        const res = await authorizedMobileEmployeeApi.insertSick(new InsertSickRequest({
            dateFrom: startDate ? new Date(startDate).toISOString().split('T')[0] : '',
            dateTo: endDate ? new Date(endDate).toISOString().split('T')[0] : '',
            timeFrom: wholeDay ? '' : (startDate ? new Date(startDate).toTimeString().slice(0, 5) : ''),
            timeTo: wholeDay ? '' : (endDate ? new Date(endDate).toTimeString().slice(0, 5) : ''),
        }));
        return { ok: res.ok ?? false, message: res.message };
    }, [startDate, endDate, authorizedMobileEmployeeApi, wholeDay]);

    const handleConfirm = useCallback(async () => {
        if (moment(startDate).isBefore(endDate)) {
            enqueueSnackbar(t('StartDateCannotBeGreaterThanEndDate'), { variant: 'error' });
            return;
        }

        let response: ICommonRangeActivityResponse = { ok: false };
        switch (rangeActivityType) {
            case '5':
                response = await insertDoctor();
                break;

            case '6':
                response = await insertHoliday();
                break;

            case '7':
                response = await insertSick();
                break;

            default:
                break;
        }

        if (response.ok) {
            enqueueSnackbar(t('RecordSetSuccessfully'), { variant: 'success' });
        } else {
            enqueueSnackbar(response.message, { variant: 'error' });
        }
    }, [startDate, endDate, rangeActivityType, enqueueSnackbar, t, insertDoctor, insertHoliday, insertSick]);

    return (
        <Box sx={{ m: 2 }}>
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 'bold' }}>{t('WholeDay')}</Typography>
                    <Switch checked={wholeDay} onChange={(e) => { setWholeDay(e.target.checked) }} />
                </Box>
                <LocalizationProviderWithLanguages>
                    {wholeDay ?
                        (
                            <Box>
                                <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>{t('StartDate')}</Typography>
                                <DatePicker value={startDate} onAccept={(e) => { setStartDate(e); setEndDate(e); }} onChange={(e) => { setStartDate(e); setEndDate(e); }} renderInput={(params) => <TextField fullWidth size='small' {...params} />} />

                                <Typography sx={{ fontSize: 16, fontWeight: 'bold', mt: 2 }}>{t('EndDate')}</Typography>
                                <DatePicker value={endDate} onAccept={(e) => { setEndDate(e) }} onChange={(e) => setEndDate(e)} renderInput={(params) => <TextField fullWidth size='small' {...params} />} />
                            </Box>
                        )
                        :
                        (
                            <Box>
                                <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>{t('StartDate')}</Typography>
                                <DateTimePicker value={startDate} onAccept={(e) => { setStartDate(e); setEndDate(e); }} onChange={(e) => { setStartDate(e); setEndDate(e); }} renderInput={(params) => <TextField fullWidth size='small' {...params} />} />

                                <Typography sx={{ fontSize: 16, fontWeight: 'bold', mt: 2 }}>{t('EndDate')}</Typography>
                                <DateTimePicker value={endDate} onAccept={(e) => { setEndDate(e) }} onChange={(e) => setEndDate(e)} renderInput={(params) => <TextField fullWidth size='small' {...params} />} />
                            </Box>
                        )
                    }
                </LocalizationProviderWithLanguages>
            </Box>
            <BottomIconButton frontIcon={<AddIcon />} title={t('SetRecord')} onClick={handleConfirm} />
        </Box>
    );
}
